import { useState, useEffect, useRef } from "react";
import { api } from "../lib/utils";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import MaintenancePanel, { useMaintenanceSelection } from "../components/MaintenancePanel";
import { Briefcase, Plus, Pencil, Globe, Upload, X, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function IPCBs() {
  const [ipcbs, setIpcbs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const { selectedItems, selectMode, clearSelection } = useMaintenanceSelection();
  const [formData, setFormData] = useState({
    name: "",
    short_name: "",
    board_name: "",
    logo_url: "",
    website: "",
    report_heading: "",
    assessor_label: "",
    acknowledgment_text: "",
  });

  useEffect(() => {
    fetchIPCBs();
  }, []);

  const fetchIPCBs = async () => {
    try {
      const response = await api.get("/ipcbs");
      setIpcbs(response.data);
    } catch (error) {
      toast.error("Failed to load IPCB boards");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const response = await api.post("/upload-logo", uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFormData({ ...formData, logo_url: response.data.logo_url });
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.short_name) {
      toast.error("Please fill in required fields (Name and Short Name)");
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/ipcbs/${editingId}`, formData);
        toast.success("IPCB updated");
      } else {
        await api.post("/ipcbs", formData);
        toast.success("IPCB created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchIPCBs();
    } catch (error) {
      console.error("IPCB creation error:", error);
      toast.error(error.response?.data?.detail || "Operation failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (ipcb) => {
    setEditingId(ipcb.ipcb_id);
    setFormData({
      name: ipcb.name || "",
      short_name: ipcb.short_name || "",
      board_name: ipcb.board_name || "",
      logo_url: ipcb.logo_url || "",
      website: ipcb.website || "",
      report_heading: ipcb.report_heading || "",
      assessor_label: ipcb.assessor_label || "",
      acknowledgment_text: ipcb.acknowledgment_text || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: "",
      short_name: "",
      board_name: "",
      logo_url: "",
      website: "",
      report_heading: "",
      assessor_label: "",
      acknowledgment_text: "",
    });
  };

  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return null;
    if (logoUrl.startsWith("http")) return logoUrl;
    return `${BACKEND_URL}${logoUrl}`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ipcbs-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">IPCB Boards</h1>
            <p className="text-slate-500 mt-1">Manage Industry Professional Certification Boards</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-ipcb-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add IPCB
              </Button>
            </DialogTrigger>
            <MaintenancePanel
              entityType="ipcbs"
              entityLabel="IPCB Boards"
              items={ipcbs}
              idField="ipcb_id"
              onRefresh={() => {
                fetchIPCBs();
                clearSelection();
              }}
              getItemLabel={(item) => item.name}
            />
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="ipcb-dialog-desc">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit IPCB Board" : "Create IPCB Board"}</DialogTitle>
                <DialogDescription id="ipcb-dialog-desc">
                  Fill in the IPCB organization details below.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Organization Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., MEEO"
                      required
                      data-testid="ipcb-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Short Name *</Label>
                    <Input
                      value={formData.short_name}
                      onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                      placeholder="e.g., MEEO"
                      required
                      data-testid="ipcb-short-name-input"
                    />
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label>IPCB Logo</Label>
                  <div className="flex items-start gap-4">
                    {formData.logo_url ? (
                      <div className="relative w-24 h-24 border rounded-lg overflow-hidden bg-slate-50">
                        <img
                          src={getLogoUrl(formData.logo_url)}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, logo_url: "" })}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        {uploading ? (
                          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-slate-400" />
                            <span className="text-xs text-slate-500 mt-1">Upload</span>
                          </>
                        )}
                      </div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleLogoUpload}
                      accept="image/*"
                      className="hidden"
                      data-testid="ipcb-logo-file-input"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-slate-500 mb-2">
                        Upload a logo image (PNG, JPG, SVG)
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="upload-ipcb-logo-btn"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Choose File
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Board/Department Name</Label>
                    <Input
                      value={formData.board_name}
                      onChange={(e) => setFormData({ ...formData, board_name: e.target.value })}
                      placeholder="e.g., Professional Standards Board"
                      data-testid="ipcb-board-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://..."
                      data-testid="ipcb-website-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Report Heading</Label>
                  <Input
                    value={formData.report_heading}
                    onChange={(e) => setFormData({ ...formData, report_heading: e.target.value })}
                    placeholder="Text for report header"
                    data-testid="ipcb-heading-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Assessor Label</Label>
                  <Input
                    value={formData.assessor_label}
                    onChange={(e) => setFormData({ ...formData, assessor_label: e.target.value })}
                    placeholder="e.g., Lead Assessor"
                    data-testid="ipcb-assessor-label-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Acknowledgment Text</Label>
                  <Textarea
                    value={formData.acknowledgment_text}
                    onChange={(e) => setFormData({ ...formData, acknowledgment_text: e.target.value })}
                    placeholder="Standard acknowledgment text for reports"
                    data-testid="ipcb-acknowledgment-input"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="save-ipcb-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      editingId ? "Update IPCB" : "Create IPCB"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-slate-100 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : ipcbs.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ipcbs.map((ipcb) => (
              <Card
                key={ipcb.ipcb_id}
                className="border-slate-200 hover:shadow-md transition-shadow"
                data-testid={`ipcb-card-${ipcb.ipcb_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    {ipcb.logo_url ? (
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center">
                        <img
                          src={getLogoUrl(ipcb.logo_url)}
                          alt={ipcb.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Briefcase className="w-6 h-6 text-amber-600" />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{ipcb.short_name}</Badge>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(ipcb)} data-testid={`edit-ipcb-${ipcb.ipcb_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{ipcb.name}</h3>
                  {ipcb.board_name && (
                    <p className="text-sm text-slate-500 mb-3">{ipcb.board_name}</p>
                  )}
                  {ipcb.website && (
                    <a
                      href={ipcb.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                    >
                      <Globe className="w-3 h-3" />
                      Website
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No IPCB Boards Yet</h3>
              <p className="text-slate-500 mb-4">Add your first IPCB organization to get started</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-ipcb-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add IPCB
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
